import { useState, useEffect } from 'react';
import { buildApiUrl, fetchWithAuth } from '@/utils/apiClient';
import { DEFAULT_PAGE_LIMIT, unwrapPaginatedItems } from '../../../hooks/useApiQuery';
import { logger } from "../../../utils/logger";

/**
 * Hook for managing patient and encounter data in the therapist panel
 * Handles loading patients, templates, and patient selection for note creation
 */
export function usePatientEncounterData({ setCurrentPatient }) {
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [showEncounterForm, setShowEncounterForm] = useState(false);
  const [patients, setPatients] = useState([]);
  const [selectedPatientForNote, setSelectedPatientForNote] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  useEffect(() => {
    loadPatients();
    loadTemplates();
  }, []);

  const loadPatients = async () => {
    try {
      const response = await fetchWithAuth(buildApiUrl(`/api/patients?limit=${DEFAULT_PAGE_LIMIT}`));
      if (response.ok) {
        const data = await response.json();
        setPatients(unwrapPaginatedItems(data));
      }
    } catch (err) {
      logger.error('Failed to load patients:', err);
    }
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await fetchWithAuth(buildApiUrl('/api/templates'));
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || data || []);
      }
    } catch (err) {
      logger.error('Failed to load templates:', err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleSelectPatient = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    if (patient) {
      setSelectedPatientForNote({ 
        id: patient.id, 
        name: patient.display_name || `${patient.first_name} ${patient.last_name}` 
      });
      setCurrentPatient({ 
        id: patient.id, 
        name: patient.display_name || `${patient.first_name} ${patient.last_name}` 
      });
      setShowEncounterForm(true);
    }
  };

  return {
    showPatientForm,
    setShowPatientForm,
    showEncounterForm,
    setShowEncounterForm,
    patients,
    setPatients,
    selectedPatientForNote,
    setSelectedPatientForNote,
    templates,
    templatesLoading,
    loadPatients,
    loadTemplates,
    handleSelectPatient
  };
}
